import React, { useRef, useState, useMemo, useEffect } from 'react';
import JoditEditor from 'jodit-react';
import { useSelector, useDispatch } from "react-redux";
import { changeTemplate, resetQuestion, setTitle, uploadCodingQuestionFile } from '../Redux/ActionCreators/testLibraryAction';
import { returnErrors } from '../Redux/ActionCreators/errorAction';

const URL_REGEX = /(https?:\/\/|www\.)[^\s<]+/gi;

const containsUrlOrImage = (html) => {
    if (!html) return false;

    // Detect <img> tags
    if (/<img\s/i.test(html)) return true;

    // Detect anchor tags
    if (/<a\s/i.test(html)) return true;

    // Detect raw URLs typed or pasted
    if (URL_REGEX.test(html)) return true;

    return false;
};

const stripUrlsAndImages = (html) => {
    let cleaned = html;

    // Remove images
    cleaned = cleaned.replace(/<img[^>]*>/gi, '');

    // Remove anchor tags but keep text
    cleaned = cleaned.replace(/<a[^>]*>(.*?)<\/a>/gi, '$1');

    // Remove raw URLs
    cleaned = cleaned.replace(URL_REGEX, '');

    return cleaned;
};

export const Editor = ({ readOnly = false, type }) => {
    const title = useSelector((state) => state.testLibrary.qTitle);
    const dispatch = useDispatch()
    const editor = useRef(null)
    const imageInputRef = useRef(null);
    const [content, setContent] = useState('')
    const AuthStr = "Bearer " + localStorage.getItem("loginToken");

    const appendDescriptionHtml = (html) => {
        let currentValue = title;
        if (editor.current) {
            // Try getting value directly
            try {
                currentValue = editor.current.value;
                if (currentValue === undefined || currentValue === null) {
                    currentValue = title;
                }
            } catch (err) {
                currentValue = title;
            }
        }

        const newValue = `${currentValue}${html}`;

        if (editor.current) {
            try {
                editor.current.value = newValue;
            } catch (e) {
                console.error("Failed to set editor value", e);
            }
        }
        setContent(newValue);
        dispatch(setTitle(newValue));
    };

    const removeDescriptionPlaceholder = (html, placeholderId) => {
        if (!html || !placeholderId) return html;
        const wrapper = document.createElement("div");
        wrapper.innerHTML = html;
        const placeholder = wrapper.querySelector(`#${placeholderId}`);
        if (placeholder) placeholder.remove();
        return wrapper.innerHTML;
    };

    const replacePlaceholderWithImage = (html, placeholderId, imageUrl) => {
        const wrapper = document.createElement("div");
        wrapper.innerHTML = html;
        const placeholder = wrapper.querySelector(`#${placeholderId}`);
        const imgWrapper = document.createElement("p");
        const img = document.createElement("img");
        img.src = imageUrl;
        img.alt = "uploaded image";
        img.style.maxWidth = "100%";
        imgWrapper.appendChild(img);
        if (placeholder) {
            placeholder.replaceWith(imgWrapper);
        } else {
            wrapper.appendChild(imgWrapper);
        }
        return wrapper.innerHTML;
    };


    const handleImageButtonClick = () => {
        if (readOnly) return;
        imageInputRef.current?.click();
    };

    const handleImageUpload = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;

        if (!file.type || !file.type.startsWith("image/")) {
            returnErrors(dispatch, "Only images are allowed", 400);
            return;
        }

        const placeholderId = `uploading-image-${Date.now()}-${Math.random()
            .toString(36)
            .slice(2, 8)}`;

        appendDescriptionHtml(
            `<p id="${placeholderId}" data-uploading-image="true">uploading...</p>`,
        );

        try {
            const res = await dispatch(uploadCodingQuestionFile(file));
            const imageUrl = res?.url;
            if (!imageUrl) {
                throw new Error("Upload failed");
            }

            let currentValue = title;
            if (editor.current) {
                try {
                    currentValue = editor.current.value;
                    if (currentValue === undefined || currentValue === null) {
                        currentValue = title;
                    }
                } catch (e) {
                    currentValue = title;
                }
            }

            const newValue = replacePlaceholderWithImage(currentValue, placeholderId, imageUrl);

            if (editor.current) {
                try {
                    editor.current.value = newValue;
                } catch (e) {
                    console.error("Failed to set editor value", e);
                }
            }
            setContent(newValue);
            dispatch(setTitle(newValue));

        } catch (err) {
            let currentValue = title;
            if (editor.current) {
                try {
                    currentValue = editor.current.value;
                    if (currentValue === undefined || currentValue === null) {
                        currentValue = title;
                    }
                } catch (e) {
                    currentValue = title;
                }
            }
            const newValue = removeDescriptionPlaceholder(currentValue, placeholderId);

            if (editor.current) {
                try {
                    editor.current.value = newValue;
                } catch (e) {
                    console.error("Failed to set editor value", e);
                }
            }
            setContent(newValue);
            dispatch(setTitle(newValue));

            returnErrors(dispatch, "Failed to upload image", 500);
        }
    };

    const config = {
        toolbarInlineForSelection: true,
        toolbar: true,
        placeholder: '',
        readonly: readOnly,
        statusbar: false,
        showCharsCounter: type === 'title',
        showWordsCounter: type === 'title',
        uploader: {
            url: `${process.env.REACT_APP_API_KEY}admin/my-content/upload/image`,
            headers: { Authorization: AuthStr },
            filesVariableName: function (e) {
                return "image";
            },
            insertImageAsBase64URI: false,
            prepareData: function (data) {
                // data.append("image", );
                return data;
            },
            isSuccess: function (resp) {
                return resp;
            },
            process: function (resp) {
                return {
                    files: resp.image,
                    path: resp.image,
                    baseurl: resp.image,
                    error: resp.error,
                    message: resp.message,
                };
            },

            defaultHandlerSuccess: function (data) {
                if (this.componentName === 'jodit-uploader') {
                    this.jodit.selection.insertImage(data.baseurl, null, 200);
                }
                else {
                    this.selection.insertImage(data.baseurl, null, 200);
                }
            }
        },
        height: 100,
        width: '100%',
        autoFocus: true,
        removeButtons: ['eraser', 'fullsize', 'file', 'video', 'image'],
        extraButtons: readOnly
            ? []
            : [
                {
                    name: "uploadImage",
                    tooltip: "Insert image",
                    icon: "image",
                    exec: handleImageButtonClick,
                },
            ],
        askBeforePasteFromWord: false,
        askBeforePasteHTML: false,
        defaultActionOnPaste: 'insert_clear_html',
        defaultActionOnPasteFromWord: 'insert_as_html',
        enableDragAndDropFileToEditor: true,
        usePopupForSpecialCharacters: true,
        // cleanHTML: { removeEmptyElements: false, },
        sizeLG: 900,
        sizeMD: 700,
        sizeSM: 400,
        buttons: [
            // 'source', '|',
            // 'insertFormula',
            'bold',
            'italic',
            'underline', '|',
            'ul',
            'ol', '|',
            'font',
            'fontsize',
            'brush',
            'paragraph', '|',
            // 'image',
            // 'table',
            // 'link', '|',
            'left',
            'center',
            'right',
            'justify', '|',
            'undo', 'redo', '|',
        ],
        buttonsMD: [
            // 'source', '|',
            'bold',
            'italic',
            'underline', '|',
            'ul',
            'ol', '|',
            'font',
            'fontsize',
            'brush',
            'paragraph', '|',
            // 'image',
            // 'table',
            // 'link', '|',
            'left',
            'center',
            'right',
            'justify', '|',
            'undo', 'redo', '|',
        ],
        buttonsSM: [
            'bold',
            'italic',
            'underline', '|',
            'ul',
            'ol', '|',
            'fontsize',
            'brush',
            'superscript',
            'subscript',
            'paragraph', '|',
            // 'image',
            // 'table',
            // 'link', '|',
            'left',
            'center',
            'right', '|',
            'undo', 'redo', '|',
        ],
        buttonsXS: [
            'bold',
            'italic',
            'underline', '|',
            'brush',
            'paragraph', '|',
            // 'image',
            'left',
            'center',
            'right', '|',
            'undo', 'redo', '|',
            'eraser'
        ],
        events: {}
    }

    useEffect(() => {
        dispatch(resetQuestion());
        dispatch(setTitle(title));
    }, [])

    return useMemo(() => (
        <>
            <JoditEditor ref={editor} value={title} config={config} onChange={content => {
                setContent(content)
                dispatch(setTitle(content))
            }} />
            <input
                type="file"
                ref={imageInputRef}
                style={{ display: "none" }}
                accept="image/*"
                onChange={handleImageUpload}
            />
        </>

    ), [readOnly, type])

}

export const TemplateEditor = (props) => {
    const { instruction } = useSelector((state) => state.testLibrary)
    const dispatch = useDispatch()
    const editor = useRef(null)
    const [content, setContent] = useState('');
    const config = {
        toolbarInlineForSelection: true,
        toolbar: true,
        placeholder: '',
        readonly: false,// all options from https://xdsoft.net/jodit/doc/
        statusbar: false,
        showCharsCounter: false,
        showWordsCounter: false,
        minHeight: 300,
        width: '100%',
        autoFocus: true,
        removeButtons: ['eraser', 'fullsize'],
        toolbarSticky: false,
        toolbarDisableStickyForMobile: false,
        allowResizeX: false,
        allowResizeY: false,
        askBeforePasteFromWord: false,
        askBeforePasteHTML: false,
        defaultActionOnPaste: 'insert_clear_html',
        defaultActionOnPasteFromWord: 'insert_as_html',
        enableDragAndDropFileToEditor: true,
        usePopupForSpecialCharacters: true,
        // cleanHTML: { removeEmptyElements: false, },
        sizeLG: 900,
        sizeMD: 700,
        sizeSM: 400,
        buttons: [
            // 'source', '|',
            // 'insertFormula',
            'bold',
            'italic',
            'underline', '|',
            'ul',
            'ol', '|',
            'font',
            'fontsize',
            'brush',
            'paragraph', '|',
            // 'image',
            // 'table',
            // 'link', '|',
            'left',
            'center',
            'right',
            'justify', '|',
            'undo', 'redo', '|',
        ],
        buttonsMD: [
            // 'source', '|',
            'bold',
            'italic',
            'underline', '|',
            'ul',
            'ol', '|',
            'font',
            'fontsize',
            'brush',
            'paragraph', '|',
            // 'image',
            // 'table',
            // 'link', '|',
            'left',
            'center',
            'right',
            'justify', '|',
            'undo', 'redo', '|',
        ],
        buttonsSM: [
            'bold',
            'italic',
            'underline', '|',
            'ul',
            'ol', '|',
            'fontsize',
            'brush',
            'superscript',
            'subscript',
            'paragraph', '|',
            // 'image',
            // 'table',
            // 'link', '|',
            'left',
            'center',
            'right', '|',
            'undo', 'redo', '|',
        ],
        buttonsXS: [
            'bold',
            'italic',
            'underline', '|',
            'brush',
            'paragraph', '|',
            // 'image',
            'left',
            'center',
            'right', '|',
            'undo', 'redo', '|',
            'eraser'
        ],
    }

    useEffect(() => {
        // dispatch(resetQuestion())
        setContent(content)
    }, [])

    return useMemo(() => (
        <JoditEditor ref={editor} value={instruction} config={config} onChange={content => {
            if (containsUrlOrImage(content)) {
                const cleaned = stripUrlsAndImages(content);

                returnErrors(
                    dispatch,
                    "Images and URLs are not allowed in the editor",
                    400
                );

                setContent(cleaned);
                dispatch(changeTemplate(cleaned, 'email'));
                return;
            }
            setContent(content)
            dispatch(changeTemplate(content, 'instruction'))
        }} />

    ), [])

}

export const BodyEditor = (props) => {
    const { emailBody } = useSelector((state) => state.testLibrary)
    const dispatch = useDispatch()
    const editor = useRef(null)
    const [content, setContent] = useState('');
    const config = {
        toolbarInlineForSelection: true,
        toolbar: true,
        placeholder: '',
        readonly: false,// all options from https://xdsoft.net/jodit/doc/
        statusbar: false,
        showCharsCounter: false,
        showWordsCounter: false,
        minHeight: 300,
        width: '100%',
        autoFocus: true,
        removeButtons: ['eraser', 'fullsize'],
        toolbarSticky: false,
        toolbarDisableStickyForMobile: false,
        allowResizeX: false,
        allowResizeY: false,
        askBeforePasteFromWord: false,
        askBeforePasteHTML: false,
        defaultActionOnPaste: 'insert_clear_html',
        defaultActionOnPasteFromWord: 'insert_as_html',
        enableDragAndDropFileToEditor: true,
        usePopupForSpecialCharacters: true,
        // cleanHTML: { removeEmptyElements: false, },
        sizeLG: 900,
        sizeMD: 700,
        sizeSM: 400,
        buttons: [
            // 'source', '|',
            // 'insertFormula',
            'bold',
            'italic',
            'underline', '|',
            'ul',
            'ol', '|',
            'font',
            'fontsize',
            'brush',
            'paragraph', '|',
            // 'image',
            // 'table',
            // 'link', '|',
            'left',
            'center',
            'right',
            'justify', '|',
            'undo', 'redo', '|',
        ],
        buttonsMD: [
            // 'source', '|',
            'bold',
            'italic',
            'underline', '|',
            'ul',
            'ol', '|',
            'font',
            'fontsize',
            'brush',
            'paragraph', '|',
            // 'image',
            // 'table',
            // 'link', '|',
            'left',
            'center',
            'right',
            'justify', '|',
            'undo', 'redo', '|',
        ],
        buttonsSM: [
            'bold',
            'italic',
            'underline', '|',
            'ul',
            'ol', '|',
            'fontsize',
            'brush',
            'superscript',
            'subscript',
            'paragraph', '|',
            // 'image',
            // 'table',
            // 'link', '|',
            'left',
            'center',
            'right', '|',
            'undo', 'redo', '|',
        ],
        buttonsXS: [
            'bold',
            'italic',
            'underline', '|',
            'brush',
            'paragraph', '|',
            // 'image',
            'left',
            'center',
            'right', '|',
            'undo', 'redo', '|',
            'eraser'
        ],
    }

    useEffect(() => {
        // dispatch(resetQuestion())
        setContent(content)
    }, [])

    return useMemo(() => (
        <JoditEditor ref={editor} value={emailBody} config={config} onChange={content => {
            if (containsUrlOrImage(content)) {
                const cleaned = stripUrlsAndImages(content);

                returnErrors(
                    dispatch,
                    "Images and URLs are not allowed in the editor",
                    400
                );

                setContent(cleaned);
                dispatch(changeTemplate(cleaned, 'email'));
                return;
            }
            setContent(content)
            dispatch(changeTemplate(content, 'email'))
        }} />

    ), [])

}

export const FooterEditor = (props) => {
    const { footer } = useSelector((state) => state.testLibrary)
    const dispatch = useDispatch()
    const editor = useRef(null)
    const [content, setContent] = useState('');
    const config = {
        toolbarInlineForSelection: true,
        toolbar: true,
        placeholder: '',
        readonly: false,// all options from https://xdsoft.net/jodit/doc/
        statusbar: false,
        showCharsCounter: false,
        showWordsCounter: false,
        minHeight: 300,
        width: '100%',
        autoFocus: true,
        removeButtons: ['eraser', 'fullsize'],
        toolbarSticky: false,
        toolbarDisableStickyForMobile: false,
        allowResizeX: false,
        allowResizeY: false,
        askBeforePasteFromWord: false,
        askBeforePasteHTML: false,
        defaultActionOnPaste: 'insert_clear_html',
        defaultActionOnPasteFromWord: 'insert_as_html',
        enableDragAndDropFileToEditor: true,
        usePopupForSpecialCharacters: true,
        // cleanHTML: { removeEmptyElements: false, },
        sizeLG: 900,
        sizeMD: 700,
        sizeSM: 400,
        buttons: [
            // 'source', '|',
            // 'insertFormula',
            'bold',
            'italic',
            'underline', '|',
            'ul',
            'ol', '|',
            'font',
            'fontsize',
            'brush',
            'paragraph', '|',
            // 'image',
            // 'table',
            // 'link', '|',
            'left',
            'center',
            'right',
            'justify', '|',
            'undo', 'redo', '|',
        ],
        buttonsMD: [
            // 'source', '|',
            'bold',
            'italic',
            'underline', '|',
            'ul',
            'ol', '|',
            'font',
            'fontsize',
            'brush',
            'paragraph', '|',
            // 'image',
            // 'table',
            // 'link', '|',
            'left',
            'center',
            'right',
            'justify', '|',
            'undo', 'redo', '|',
        ],
        buttonsSM: [
            'bold',
            'italic',
            'underline', '|',
            'ul',
            'ol', '|',
            'fontsize',
            'brush',
            'superscript',
            'subscript',
            'paragraph', '|',
            // 'image',
            // 'table',
            // 'link', '|',
            'left',
            'center',
            'right', '|',
            'undo', 'redo', '|',
        ],
        buttonsXS: [
            'bold',
            'italic',
            'underline', '|',
            'brush',
            'paragraph', '|',
            // 'image',
            'left',
            'center',
            'right', '|',
            'undo', 'redo', '|',
            'eraser'
        ],
    }

    useEffect(() => {
        // dispatch(resetQuestion())
        setContent(content)
    }, [])

    return useMemo(() => (
        <JoditEditor ref={editor} value={footer} config={config} onChange={content => {
            if (containsUrlOrImage(content)) {
                const cleaned = stripUrlsAndImages(content);

                returnErrors(
                    dispatch,
                    "Images and URLs are not allowed in the editor",
                    400
                );

                setContent(cleaned);
                dispatch(changeTemplate(cleaned, 'email'));
                return;
            }
            setContent(content)
            dispatch(changeTemplate(content, 'footer'))
        }} />

    ), [])

}
